import { Component } from 'react';
import type { ReactNode, ErrorInfo } from 'react';
import * as Sentry from '@sentry/react';

interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
  featureName: string;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

function DefaultErrorFallback({
  featureName,
  onReset,
}: {
  featureName: string;
  onReset: () => void;
}) {
  return (
    <div className="flex items-center justify-center p-8 bg-[#F8F9FB] dark:bg-gray-950 min-h-[200px]">
      <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-lg border border-gray-100 dark:border-gray-800 p-8 max-w-md w-full text-center">
        <div className="w-12 h-12 mx-auto mb-4 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
          <span className="text-red-600 dark:text-red-400 text-xl font-bold">!</span>
        </div>
        <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-2">
          Algo salió mal en {featureName}
        </h2>
        <p className="text-gray-600 dark:text-gray-400 mb-6">
          Ocurrió un error inesperado en este módulo. Puedes intentar recuperarlo sin recargar la página.
        </p>
        <button
          onClick={onReset}
          className="bg-[#6D5DFE] hover:bg-[#5B4DE0] text-white px-6 py-2.5 rounded-xl shadow-md shadow-purple-200 dark:shadow-purple-900/30 transition-all font-medium"
        >
          Reintentar
        </button>
      </div>
    </div>
  );
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
    this.handleReset = this.handleReset.bind(this);
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    console.error(`[ErrorBoundary:${this.props.featureName}]`, error, info);
    Sentry.captureException(error, {
      extra: {
        featureName: this.props.featureName,
        componentStack: info.componentStack,
      },
    });
  }

  handleReset(): void {
    this.setState({ hasError: false, error: null });
  }

  render(): ReactNode {
    if (this.state.hasError) {
      if (this.props.fallback !== undefined) {
        return this.props.fallback;
      }
      return (
        <DefaultErrorFallback
          featureName={this.props.featureName}
          onReset={this.handleReset}
        />
      );
    }
    return this.props.children;
  }
}
